/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsCascadePicker = require('./PictProvider-InputType-MaterialsCascadePicker.js');
const ScopedEntityData = require('./_ScopedEntityData.js');

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'MaterialsLineItemPicker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
	Entity: 'LineItem',
};

/**
 * The LineItem picker (beast B4 — a JOIN cascade): the Picker-stack port of MaterialsLineItemSelector,
 * mirroring the legacy provider 1:1 on the MaterialsCascadePicker base. Multi-select by default;
 * selecting line items reconciles the sibling SampleLineItemJoin array (reuse-by-IDLineItem so server
 * ids and the entered RepresentedQuantity survive re-selection) and stamps the display-only
 * `LineItemUnits` onto each join row for the tabular units column. On the bound sample's SampleChanged
 * it re-derives value + joins from the DB; the ProjectEntityDataRecords sweep projects the joins
 * (display fields stripped) into _EntityData.
 */
class PictProviderInputTypeMaterialsLineItemPicker extends MaterialsCascadePicker
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Pict & { TransactionTracking: any, TempData: any }} */
		this.pict;
	}

	/**
	 * Project scoping + Name sort, ported from the legacy hook: the legacy scoped to the BOUND SAMPLE's
	 * project first (document project as fallback), so the base broker's document-project LineItem clause
	 * is suppressed for the super call and the sample-preferred clause applied here (existing clauses
	 * paren-wrapped so it ANDs the whole set, like the legacy).
	 * @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex] @param {Object} [pView]
	 * @return {{Filters: Array<string>, BackOffFilters: Array<string>}}
	 */
	getContextualSearchFilters(pInput, pRowIndex, pView)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		const tmpPriorFlag = tmpPictForm.disableProjectFilteringForEntity;
		tmpPictForm.disableProjectFilteringForEntity = true;
		let tmpSplit;
		try
		{
			tmpSplit = super.getContextualSearchFilters(pInput, pRowIndex, pView);
		}
		finally
		{
			tmpPictForm.disableProjectFilteringForEntity = tmpPriorFlag;
		}
		const tmpFilters = tmpSplit.Filters;
		if (!tmpPictForm.disableProjectFilteringForEntity)
		{
			const tmpSample = this._getBoundSampleRecord(pView, pInput);
			const tmpProjectID = tmpSample?.IDProject || this.pict.AppData.DocumentData?.ReportData?.IDProject;
			if (tmpProjectID > 0)
			{
				if (tmpFilters.length > 0)
				{
					//FIXME: handle existing sort instructions (FSF)
					tmpFilters.unshift('FOP~0~(~0');
					tmpFilters.push('FCP~0~)~0');
				}
				tmpFilters.push(`FBV~IDProject~EQ~${tmpProjectID}`);
			}
		}
		tmpFilters.push('FSF~Name~ASC~0');
		return tmpSplit;
	}

	/** Init: legacy defaults (multi-select, ItemCode EntityTag) + the configured family tracking GUID. */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._applyLineItemDefaults(pInput);
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		// sample is in charge of the family for tracking
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, null, pInput.PictForm.SampleTrackingGUID);
		}
		return tmpResult;
	}

	/** Tabular twin of the init. */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._applyLineItemDefaults(pInput);
		const tmpResult = super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, pRowIndex, pInput.PictForm.SampleTrackingGUID);
		}
		return tmpResult;
	}

	/** A sample can represent several line items (multi-select), tag on ItemCode. @param {Record<string, any>} pInput */
	_applyLineItemDefaults(pInput)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.Multiple == null)
		{
			pInput.PictForm.Multiple = true;
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'ItemCode';
		}
	}

	/**
	 * Reconcile the selected LineItems against the existing SampleLineItemJoins: reuse matches (by
	 * IDLineItem — preserving server ids + the entered RepresentedQuantity), synthesize new (GUID minted
	 * off the document + salt + IDLineItem; saved-vs-unsaved sample keying), drop deselected, then stamp
	 * the display-only units column. Ported 1:1.
	 * @param {Array<Record<string, any>>} pLineItems @param {Array<Record<string, any>>} pCurrentSampleLineItemJoins
	 * @param {Record<string, any>|null} pSample @param {Object} pView @param {Record<string, any>} pInput
	 * @param {number|string|null} pRowIndex @return {Array<Record<string, any>>}
	 */
	_syncSampleLineItemJoins(pLineItems, pCurrentSampleLineItemJoins, pSample, pView, pInput, pRowIndex)
	{
		const tmpUnclaimedJoins = Array.isArray(pCurrentSampleLineItemJoins) ? pCurrentSampleLineItemJoins.slice() : [ ];
		const tmpSampleLineItemJoins = [ ];
		for (const tmpLineItem of pLineItems || [])
		{
			if (!tmpLineItem || typeof tmpLineItem !== 'object')
			{
				continue;
			}
			const tmpExistingJoinIndex = tmpUnclaimedJoins.findIndex((pJoin) => pJoin.IDLineItem == tmpLineItem.IDLineItem);
			if (tmpExistingJoinIndex >= 0)
			{
				tmpSampleLineItemJoins.push(tmpUnclaimedJoins[tmpExistingJoinIndex]);
				tmpUnclaimedJoins.splice(tmpExistingJoinIndex, 1);
				continue;
			}
			const tmpNewJoin = JSON.parse(JSON.stringify(pInput.PictForm.DefaultSampleLineItemJoinRecord ||
				{
					GUIDSampleLineItemJoin: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-SLIJ-${this._getSalt(pView, pInput, pRowIndex)}`,
					IDLineItem: tmpLineItem.IDLineItem,
					RepresentedQuantity: '0.000',
				}));
			tmpNewJoin.GUIDSampleLineItemJoin += `-${tmpLineItem.IDLineItem}`;
			if (pSample && pSample.GUIDSample)
			{
				if (pSample.IDSample > 0)
				{
					tmpNewJoin.IDSample = pSample.IDSample;
				}
				else
				{
					tmpNewJoin.GUIDSample = pSample.GUIDSample;
				}
			}
			this._translateRecordTemplateFields(tmpNewJoin);

			if (!tmpNewJoin.GUIDSampleLineItemJoin)
			{
				tmpNewJoin.GUIDSampleLineItemJoin = this.pict.getUUID();
			}
			tmpSampleLineItemJoins.push(tmpNewJoin);
		}
		this._stampJoinDisplayFields(tmpSampleLineItemJoins, pLineItems);
		return tmpSampleLineItemJoins;
	}

	/**
	 * Stamp a display-only flat `LineItemUnits` onto each join row (the LineItem's unit, matched by
	 * IDLineItem) so the tabular Rep Qty Units column reads it directly as plain text — a 2nd
	 * EntityDisplay on the same LineItem doesn't render. NOT a SampleLineItemJoin column —
	 * `_syncRecordsToEntityData` MUST strip it or the upsert breaks. Ported 1:1.
	 * @param {Array<Record<string, any>>} pSampleLineItemJoins
	 * @param {Array<Record<string, any>>} pLineItems - The selected LineItem records (carry `Units`).
	 */
	_stampJoinDisplayFields(pSampleLineItemJoins, pLineItems)
	{
		if (!Array.isArray(pSampleLineItemJoins))
		{
			return;
		}
		const tmpUnitsByLineItem = { };
		for (const tmpLineItem of (pLineItems || [ ]))
		{
			if (tmpLineItem && tmpLineItem.IDLineItem != null)
			{
				tmpUnitsByLineItem[tmpLineItem.IDLineItem] = tmpLineItem.Units;
			}
		}
		for (const tmpJoin of pSampleLineItemJoins)
		{
			if (tmpJoin && typeof tmpJoin === 'object' && tmpUnitsByLineItem[tmpJoin.IDLineItem] != null)
			{
				tmpJoin.LineItemUnits = tmpUnitsByLineItem[tmpJoin.IDLineItem];
			}
		}
	}

	/**
	 * A de-selected LineItem takes its `SampleLineItemJoin` with it. Picker twin of the selector hook.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {Set<string>} pRetainedIdentities
	 * @return {Array<{ Entity: string, Record: Record<string, any> }>}
	 */
	_collectRemovedDependentRecords(pView, pInput, pRowIndex, pRetainedIdentities)
	{
		const tmpRecordAddress = this._getRecordAddress(pInput, pRowIndex);
		const tmpJoinsAddress = pInput.PictForm.SampleLineItemJoinAddress || `${tmpRecordAddress}_SampleJoins`;
		const tmpJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpJoinsAddress);
		if (!Array.isArray(tmpJoins))
		{
			return [ ];
		}
		const tmpRemovals = [ ];
		for (const tmpJoin of tmpJoins)
		{
			if (tmpJoin && typeof tmpJoin === 'object' && !pRetainedIdentities.has(String(tmpJoin.IDLineItem)))
			{
				tmpRemovals.push({ Entity: 'SampleLineItemJoin', Record: tmpJoin });
			}
		}
		return tmpRemovals;
	}

	/**
	 * The LineItem cascade — the port of the legacy `_onRecordRefresh`: reconcile the SampleLineItemJoin
	 * array beside the record (clear → drop all joins), repaint dependents in-section, then super (the
	 * family bus fan-out). A cleared picker hands the seam `{}`/`[]` — normalized to "no line items"
	 * like the legacy's falsy-record branch.
	 * @param {Object} pView @param {Record<string, any>} pInput
	 * @param {Record<string, any>|Array<Record<string, any>>} pRecord
	 * @param {number|string} [pRowIndex] @param {string} [pTransactionGUID]
	 */
	_onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID)
	{
		const tmpLineItemAddress = this._getRecordAddress(pInput, pRowIndex);
		const tmpLineItems = Array.isArray(pRecord) ? pRecord :
			((pRecord && typeof pRecord === 'object' && Object.keys(pRecord).length > 0) ? [ pRecord ] : [ ]);
		const tmpSampleLineItemJoinsAddress = pInput.PictForm.SampleLineItemJoinAddress || `${tmpLineItemAddress}_SampleJoins`;
		let tmpSampleLineItemJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLineItemJoinsAddress);
		const tmpSample = this._getBoundSampleRecord(pView, pInput);
		if (!Array.isArray(tmpSampleLineItemJoins))
		{
			if (tmpSampleLineItemJoins)
			{
				this.log.error(`MaterialsLineItemPicker: SampleLineItemJoins at address ${tmpSampleLineItemJoinsAddress} is not an array`, { tmpSampleLineItemJoins });
			}
			tmpSampleLineItemJoins = [ ];
		}
		tmpSampleLineItemJoins = this._syncSampleLineItemJoins(tmpLineItems, tmpSampleLineItemJoins, tmpSample, pView, pInput, pRowIndex);
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLineItemJoinsAddress, tmpSampleLineItemJoins);
		// ensures any fields that use this record are updated immediately, if they are in the same section
		if (typeof pView.marshalToView === 'function')
		{
			pView.marshalToView();
		}
		super._onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID);
	}

	/**
	 * Family + save-sweep events — ported 1:1 from the legacy `_handleEvent`:
	 *   - SampleChanged (tracking-matched): re-derive this selector from the DB — load the sample's
	 *     SampleLineItemJoins, land them at the join address, and commit the joined line-item ids as the
	 *     value (the programmatic set re-runs the provider loop, so the join reconcile rides the same
	 *     path a user pick does). No/invalid sample resets the joins. The fence op is registered only
	 *     when a fetch actually runs (the legacy registered before its IDSample check and leaked the op —
	 *     unreachable-hang family, same reasoned-divergence lineage as B2's clear-reset).
	 *   - ProjectEntityDataRecords: project the joins (+ any sibling DocumentPolyJoin) into the sweep
	 *     target.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue @param {string} pHTMLSelector
	 * @param {number|string|null} pRowIndex @param {string} pEvent @param {string} pTransactionGUID
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!pEvent || typeof pEvent !== 'string')
		{
			return;
		}

		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload ] = pEvent.split(':');

		if (tmpEventType === 'SampleChanged')
		{
			if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex))
			{
				return;
			}
			if (!this._shouldRefreshSampleJoins(pInput))
			{
				return;
			}
			const tmpLineItemAddress = this._getRecordAddress(pInput, pRowIndex);
			const tmpSampleLineItemJoinAddress = pInput.PictForm.SampleLineItemJoinAddress || `${tmpLineItemAddress}_SampleJoins`;
			const tmpSampleRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload);
			if (!tmpSampleRecord || typeof tmpSampleRecord !== 'object' || Array.isArray(tmpSampleRecord))
			{
				this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLineItemJoinAddress, []);
				return;
			}
			if (!tmpSampleRecord.IDSample)
			{
				return;
			}
			const tmpRefreshEvent = 'SampleLineItemJoinRefresh';
			pView.registerEventTransactionAsyncOperation(pTransactionGUID, tmpRefreshEvent);
			this.pict.HeadlightRestClient.getJSON(`SampleLineItemJoins/FilteredTo/FBV~IDSample~EQ~${tmpSampleRecord.IDSample}`, (pError, pRecords) =>
			{
				// A failed fetch is not evidence of "no line items". Treating it as an empty set and writing
				// that through overwrites the persisted selection on a transient network error -- and the
				// document auto-save then persists the loss with no user action. Abort instead: leave both the
				// joins address and the input value exactly as loaded.
				if (pError || !Array.isArray(pRecords))
				{
					this.log.error(`MaterialsLineItemPicker: failed to load SampleLineItemJoins for Sample ID ${tmpSampleRecord.IDSample} -- keeping the persisted value.`, { pError, pRecords });
					pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
					return;
				}
				const tmpLineItemValue = pRecords.map((pJoin) => pJoin.IDLineItem).join(',');
				// Fail-open backstop, same instinct as the entity selector's autofill-scope vet: an empty result
				// cannot be distinguished from "the joins could not be resolved", so it must not clobber a value
				// the document already carries. Only an explicit user action may empty this. A non-empty result
				// is authoritative and still corrects a stale selection.
				const tmpExistingValue = this.pict.manifest.getValueAtAddress(
					pView.getMarshalDestinationObject(), this._getRecordAddress(pInput, pRowIndex, ''));
				if (tmpLineItemValue === '' && String(tmpExistingValue ?? '').trim() !== '')
				{
					this._dropSoftDeletedHeldJoins(pView, pInput, pRowIndex,
						{ Entity: 'SampleLineItemJoin', ValueField: 'IDLineItem', JoinAddress: tmpSampleLineItemJoinAddress, Value: tmpExistingValue, Label: 'MaterialsLineItemPicker' },
						(pDropped) =>
						{
							if (pDropped < 1)
							{
								this.log.warn(`MaterialsLineItemPicker: Sample ID ${tmpSampleRecord.IDSample} resolved no SampleLineItemJoins but the document carries [${tmpExistingValue}] -- keeping the persisted value.`);
							}
							pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
						});
					return;
				}
				this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLineItemJoinAddress, pRecords);
				// Re-binding to a different Sample replaces this input's joins wholesale; the outgoing sample's
				// joins stay attached to that sample and are not this form's to delete.
				ScopedEntityData.beginProgrammaticLoad(this.pict);
				try
				{
					this._setValueAndCommit(pView, pInput, tmpLineItemValue, pRowIndex);
				}
				finally
				{
					ScopedEntityData.endProgrammaticLoad(this.pict);
				}
				pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
			});
			return;
		}

		if (tmpEventType === 'ProjectEntityDataRecords')
		{
			const tmpTarget = this._getProjectionTarget(pInput, pEvent);
			if (!tmpTarget)
			{
				return;
			}
			this._syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
			const tmpLineItemAddress = this._getRecordAddress(pInput, pRowIndex);
			const tmpDocumentPolyJoinAddress = pInput.PictForm.DocumentPolyJoinAddress || `${tmpLineItemAddress}_DocumentPolyJoin`;
			const tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpDocumentPolyJoinAddress);
			if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin) && tmpTarget && typeof tmpTarget === 'object' && !Array.isArray(tmpTarget))
			{
				if (!tmpTarget.DocumentPolyJoin || typeof tmpTarget.DocumentPolyJoin !== 'object' || Array.isArray(tmpTarget.DocumentPolyJoin))
				{
					tmpTarget.DocumentPolyJoin = { };
				}
				tmpTarget.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
			}
		}
	}

	/**
	 * Project the SampleLineItemJoin array into the entity-data collection (keyed by
	 * GUIDSampleLineItemJoin), stripping the display-only unit fields that are not join columns (they
	 * would break the upsert). The plural counterpart of the base _syncRecordToEntityData (the LineItem
	 * CATALOG record itself never projects; the joins are the created data). Ported 1:1.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 * @param {string} pTransactionGUID @param {Object} pTargetObject
	 */
	_syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, pTargetObject)
	{
		const tmpLineItemAddress = this._getRecordAddress(pInput, pRowIndex);
		const tmpEntityData = pTargetObject;
		if (!tmpEntityData || typeof tmpEntityData !== 'object' || Array.isArray(tmpEntityData))
		{
			return;
		}

		const tmpSampleLineItemJoinsAddress = pInput.PictForm.SampleLineItemJoinAddress || `${tmpLineItemAddress}_SampleJoins`;
		const tmpSampleLineItemJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLineItemJoinsAddress);
		if (Array.isArray(tmpSampleLineItemJoins))
		{
			if (!tmpEntityData.SampleLineItemJoin || typeof tmpEntityData.SampleLineItemJoin !== 'object' || Array.isArray(tmpEntityData.SampleLineItemJoin))
			{
				tmpEntityData.SampleLineItemJoin = { };
			}
			for (const tmpSampleLineItemJoin of tmpSampleLineItemJoins)
			{
				const tmpPersistedJoin = { ...tmpSampleLineItemJoin };
				// Display-only fields (current + legacy attempts) — NOT SampleLineItemJoin columns; would break the upsert.
				delete tmpPersistedJoin.LineItemUnits;
				delete tmpPersistedJoin.IDLineItemUnits;
				delete tmpPersistedJoin.RepQtyUnits;
				tmpEntityData.SampleLineItemJoin[tmpPersistedJoin.GUIDSampleLineItemJoin] = tmpPersistedJoin;
			}
		}
	}

	/**
	 * The bound Sample record: ask the family over the bus (PushRecord into TempData), falling back to
	 * the conventional SampleAddress. Ported 1:1 (pView may be absent on a BaseFilter-closure call —
	 * the bus path needs no view).
	 * @param {Object|null} pView @param {Record<string, any>} pInput @return {Record<string, any>|null}
	 */
	_getBoundSampleRecord(pView, pInput)
	{
		const tmpPayloadUUID = this.pict.getUUID();
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`PushRecord:${this._getTrackingGUID(pInput)}:TempData["${tmpPayloadUUID}"]`);

		let tmpSample = this.pict.TempData[tmpPayloadUUID];
		delete this.pict.TempData[tmpPayloadUUID];

		if (!tmpSample || typeof tmpSample !== 'object' || Array.isArray(tmpSample))
		{
			//FIXME: what we actually need is a way to find this via the tracking GUID
			if (!pView || typeof pView.getMarshalDestinationObject !== 'function')
			{
				return null;
			}
			const tmpSampleAddress = pInput.PictForm.SampleAddress || 'SampleID__Record';
			tmpSample = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleAddress);
		}
		return tmpSample;
	}
}

module.exports = PictProviderInputTypeMaterialsLineItemPicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
